import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { DollarSign, Building, Bell, ShieldCheck, Plus, Trash2, Send, RefreshCw, KeyRound, Plug } from "lucide-react";
import { QuickBooksConnectionCard } from "@/components/QuickBooksConnectionCard";
import { QuickBooksMapeosCard } from "@/components/QuickBooksMapeosCard";

const ALERT_TYPE_LABELS = {
  cobro_vencido: "Cobro vencido", duplicado_detectado: "Duplicado detectado",
  margen_excedido: "Margen excedido", cobro_registrado: "Cobro registrado",
  pago_registrado: "Pago registrado", load_desbloqueado: "Load desbloqueado",
};
const ALL_TIPOS_ALERTA = Object.keys(ALERT_TYPE_LABELS);
const ALL_CANALES = ["email", "sms", "whatsapp"];
const CANAL_LABELS = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };

const emptyRecipient = () => ({ nombre: "", email: "", telefono: "", tipos_alerta: [...ALL_TIPOS_ALERTA], canales: ["email"] });

const MODULES = ["dashboard", "loads", "clients", "carriers", "rate_cards", "documents", "alerts", "audit", "users", "companies", "settings", "reports", "cruces", "unidades", "cajas", "referencias_cummins", "guias", "facturacion_qbo"];
const ACTIONS = ["ver", "crear", "editar", "desactivar"];
const ROLES = ["dueno", "administracion", "operacion", "consulta"];
const ROLE_LABELS = { dueno: "Dueño", administracion: "Admin.", operacion: "Operación", consulta: "Consulta" };

export default function Config() {
  const [settings, setSettings] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [perms, setPerms] = useState([]);
  const [qboConnections, setQboConnections] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedRole, setSelectedRole] = useState("administracion");
  const [secrets, setSecrets] = useState({ resend_api_key: "", twilio_account_sid: "", twilio_auth_token: "" });
  const [testCanal, setTestCanal] = useState("email");
  const [testDestinatario, setTestDestinatario] = useState("");
  const [testing, setTesting] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const loadQbo = useCallback(async () => {
    try {
      const { data } = await api.get("/quickbooks/connections");
      setQboConnections(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("qbo_ok")) {
      toast.success("QuickBooks conectado correctamente");
      loadQbo();
      navigate("/config", { replace: true });
    } else if (params.get("qbo_error")) {
      toast.error(`No se pudo conectar QuickBooks: ${decodeURIComponent(params.get("qbo_error"))}`);
      navigate("/config", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = useCallback(async () => {
    try {
      const [s, a, p] = await Promise.all([
        api.get("/settings"), api.get("/alert-settings"), api.get("/permissions"),
      ]);
      setSettings(s.data); setAlerts(a.data); setPerms(p.data);
      loadQbo();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [loadQbo]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/settings", {
        tipo_cambio_usd_mxn: parseFloat(settings.tipo_cambio_usd_mxn),
        nombre_empresa: settings.nombre_empresa,
        logo_url: settings.logo_url,
        modelo_ia: settings.modelo_ia,
        umbral_margen_verde: parseFloat(settings.umbral_margen_verde),
        umbral_margen_amarillo: parseFloat(settings.umbral_margen_amarillo),
        zona_horaria: settings.zona_horaria,
        modo_demo: settings.modo_demo,
      });
      toast.success("Configuración guardada");
      window.dispatchEvent(new CustomEvent("trebol:settings-updated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAlerts = async () => {
    try {
      const payload = {
        dias_sin_cobrar_aviso_1: parseInt(alerts.dias_sin_cobrar_aviso_1),
        dias_sin_cobrar_aviso_2: parseInt(alerts.dias_sin_cobrar_aviso_2),
        dias_sin_cobrar_critico: parseInt(alerts.dias_sin_cobrar_critico),
        dias_criticos_post_vencimiento: parseInt(alerts.dias_criticos_post_vencimiento),
        hora_envio_diario: alerts.hora_envio_diario,
        canal_email_activo: alerts.canal_email_activo,
        canal_sms_activo: alerts.canal_sms_activo,
        canal_whatsapp_activo: alerts.canal_whatsapp_activo,
        canal_inapp_activo: alerts.canal_inapp_activo,
        modo_simulacion_email: alerts.modo_simulacion_email,
        modo_simulacion_sms: alerts.modo_simulacion_sms,
        modo_simulacion_whatsapp: alerts.modo_simulacion_whatsapp,
        resend_from_email: alerts.resend_from_email,
        resend_from_name: alerts.resend_from_name,
        twilio_sms_from: alerts.twilio_sms_from,
        twilio_whatsapp_from: alerts.twilio_whatsapp_from,
        whatsapp_templates: alerts.whatsapp_templates,
        destinatarios: alerts.destinatarios,
      };
      if (secrets.resend_api_key) payload.resend_api_key = secrets.resend_api_key;
      if (secrets.twilio_account_sid) payload.twilio_account_sid = secrets.twilio_account_sid;
      if (secrets.twilio_auth_token) payload.twilio_auth_token = secrets.twilio_auth_token;
      await api.put("/alert-settings", payload);
      setSecrets({ resend_api_key: "", twilio_account_sid: "", twilio_auth_token: "" });
      toast.success("Alertas guardadas");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addRecipient = () => setAlerts({ ...alerts, destinatarios: [...(alerts.destinatarios || []), emptyRecipient()] });
  const removeRecipient = (idx) => setAlerts({ ...alerts, destinatarios: alerts.destinatarios.filter((_, i) => i !== idx) });
  const updateRecipient = (idx, field, value) => {
    const next = [...alerts.destinatarios];
    next[idx] = { ...next[idx], [field]: value };
    setAlerts({ ...alerts, destinatarios: next });
  };
  const toggleRecipientList = (idx, field, value) => {
    const next = [...alerts.destinatarios];
    const cur = next[idx][field] || [];
    next[idx] = { ...next[idx], [field]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    setAlerts({ ...alerts, destinatarios: next });
  };
  const updateTemplate = (tipo, field, value) => {
    setAlerts({ ...alerts, whatsapp_templates: { ...alerts.whatsapp_templates, [tipo]: { ...alerts.whatsapp_templates[tipo], [field]: value } } });
  };

  const runOverdueCheck = async () => {
    setRunningCheck(true);
    try {
      const { data } = await api.post("/alert-settings/run-check");
      toast.success(`Verificación ejecutada: ${data.alertas_creadas} alerta(s) nueva(s) creada(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunningCheck(false); }
  };

  const sendTestAlert = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/alert-settings/test", { canal: testCanal, destinatario: testDestinatario || null });
      if (data.ok === false) toast.error(data.message || "No se pudo enviar la alerta de prueba");
      else toast.success(`Alerta de prueba ${data.envios?.[0]?.simulada ? "simulada" : "enviada"} — revisá la pantalla de Alertas para ver el contenido exacto`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const hasPerm = (rol, modulo, accion) => perms.some((p) => p.rol === rol && p.modulo === modulo && p.accion === accion);
  const togglePerm = (rol, modulo, accion) => {
    setPerms((prev) => {
      const exists = prev.some((p) => p.rol === rol && p.modulo === modulo && p.accion === accion);
      if (exists) return prev.filter((p) => !(p.rol === rol && p.modulo === modulo && p.accion === accion));
      return [...prev, { rol, modulo, accion }];
    });
  };
  const savePerms = async () => {
    try {
      await api.put("/permissions", { permisos: perms });
      toast.success("Permisos guardados");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!settings || !alerts) return <div className="text-sm text-muted-foreground">Cargando...</div>;

  return (
    <div>
      <PageHeader title="Configuración" subtitle="Parámetros del sistema (solo Dueño)" />
      <Tabs defaultValue={new URLSearchParams(location.search).get("tab") || "general"}>
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-alertas">Alertas</TabsTrigger>
          <TabsTrigger value="integraciones" data-testid="tab-integraciones">Integraciones</TabsTrigger>
          <TabsTrigger value="permisos" data-testid="tab-permisos">Permisos</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-5 space-y-5">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><DollarSign className="h-5 w-5 text-primary" /> Tipo de cambio</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-xs">
                <Label className="text-xs text-muted-foreground">USD → MXN</Label>
                <Input className="mt-1 font-mono" type="number" step="0.0001"
                  value={settings.tipo_cambio_usd_mxn}
                  onChange={(e) => setSettings({ ...settings, tipo_cambio_usd_mxn: e.target.value })}
                  data-testid="config-tipo-cambio" />
                <p className="mt-1 text-xs text-muted-foreground">
                  Última actualización: {settings.tipo_cambio_actualizado_en ? new Date(settings.tipo_cambio_actualizado_en).toLocaleString("es-MX") : "—"}
                </p>
              </div>
              <div className="grid max-w-md grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Umbral margen verde (%)</Label>
                  <Input className="mt-1 font-mono" type="number" value={settings.umbral_margen_verde} onChange={(e) => setSettings({ ...settings, umbral_margen_verde: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Umbral margen amarillo (%)</Label>
                  <Input className="mt-1 font-mono" type="number" value={settings.umbral_margen_amarillo} onChange={(e) => setSettings({ ...settings, umbral_margen_amarillo: e.target.value })} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Building className="h-5 w-5 text-primary" /> Datos de la empresa</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-md">
                <Label className="text-xs text-muted-foreground">Nombre de la empresa</Label>
                <Input className="mt-1" value={settings.nombre_empresa} onChange={(e) => setSettings({ ...settings, nombre_empresa: e.target.value })} data-testid="config-nombre-empresa" />
              </div>
              <div className="max-w-md">
                <Label className="text-xs text-muted-foreground">Logo (URL)</Label>
                <Input className="mt-1" value={settings.logo_url || ""} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} />
              </div>
              <div className="max-w-md">
                <Label className="text-xs text-muted-foreground">Modelo de IA (lectura de documentos)</Label>
                <Input className="mt-1 font-mono" value={settings.modelo_ia || ""} onChange={(e) => setSettings({ ...settings, modelo_ia: e.target.value })} data-testid="config-modelo-ia" />
                {settings.ultimo_modelo_ia_usado && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="config-ultimo-modelo-usado">
                    Ultima extraccion usó: <span className="font-mono">{settings.ultimo_modelo_ia_usado}</span>
                    {settings.ultima_extraccion_en && ` el ${new Date(settings.ultima_extraccion_en).toLocaleString("es-MX")}`}
                  </p>
                )}
              </div>
              <div className="max-w-md">
                <Label className="text-xs text-muted-foreground">Zona horaria del negocio</Label>
                <Input className="mt-1 font-mono" value={settings.zona_horaria || ""} placeholder="America/Monterrey"
                  onChange={(e) => setSettings({ ...settings, zona_horaria: e.target.value })} data-testid="config-zona-horaria" />
                <p className="mt-1 text-xs text-muted-foreground">
                  Usada para calcular vencimientos, "hoy" y la hora de envío del resumen diario (no UTC).
                </p>
              </div>
              <label className="flex max-w-md items-center gap-2 text-sm">
                <Checkbox checked={!!settings.modo_demo} onCheckedChange={(v) => setSettings({ ...settings, modo_demo: !!v })}
                          data-testid="config-modo-demo" />
                Mostrar aviso de "datos de ejemplo" en toda la app (desactivalo cuando carguen datos reales del cliente)
              </label>
            </CardContent>
          </Card>
          <Button onClick={saveSettings} data-testid="config-save-general">Guardar cambios</Button>
        </TabsContent>

        <TabsContent value="alertas" className="mt-5 space-y-5">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Bell className="h-5 w-5 text-primary" /> Umbrales de cobro vencido</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid max-w-lg grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Aviso 1 (días)</Label>
                  <Input className="mt-1 font-mono" type="number" value={alerts.dias_sin_cobrar_aviso_1} onChange={(e) => setAlerts({ ...alerts, dias_sin_cobrar_aviso_1: e.target.value })} data-testid="config-aviso1" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Aviso 2 (días)</Label>
                  <Input className="mt-1 font-mono" type="number" value={alerts.dias_sin_cobrar_aviso_2} onChange={(e) => setAlerts({ ...alerts, dias_sin_cobrar_aviso_2: e.target.value })} data-testid="config-aviso2" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Crítico (días)</Label>
                  <Input className="mt-1 font-mono" type="number" value={alerts.dias_sin_cobrar_critico} onChange={(e) => setAlerts({ ...alerts, dias_sin_cobrar_critico: e.target.value })} data-testid="config-critico" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Nota: si el cliente tiene términos de pago cargados, el vencimiento se cuenta contra esos términos; si no, contra estos días default desde el delivery.
              </p>
              <div className="max-w-xs">
                <Label className="text-xs text-muted-foreground">Días post-vencimiento para pasar a "Crítico"</Label>
                <Input className="mt-1 font-mono" type="number" value={alerts.dias_criticos_post_vencimiento}
                  onChange={(e) => setAlerts({ ...alerts, dias_criticos_post_vencimiento: e.target.value })} data-testid="config-dias-criticos" />
                <p className="mt-1 text-xs text-muted-foreground">
                  Cuando un load lleva vencido más de estos días (contra la fecha de vencimiento del cliente), pasa de "Vencido" (naranja) a "Crítico" (rojo).
                </p>
              </div>
              <div className="max-w-xs">
                <Label className="text-xs text-muted-foreground">Hora de envío del resumen diario</Label>
                <Input className="mt-1 font-mono" value={alerts.hora_envio_diario} onChange={(e) => setAlerts({ ...alerts, hora_envio_diario: e.target.value })} data-testid="config-hora-resumen" />
              </div>
              <Button variant="outline" size="sm" onClick={runOverdueCheck} disabled={runningCheck} className="gap-2" data-testid="config-run-overdue-check">
                <RefreshCw className="h-3.5 w-3.5" /> {runningCheck ? "Ejecutando..." : "Ejecutar verificación de vencidos ahora"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Bell className="h-5 w-5 text-primary" /> Canales</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">Cada canal tiene su propio switch de modo simulación — pueden convivir canales reales y simulados al mismo tiempo.</p>
              {ALL_CANALES.concat(["inapp"]).map((canal) => (
                <div key={canal} className="flex flex-wrap items-center gap-4 rounded-md border border-border p-3">
                  <label className="flex w-40 items-center gap-2 text-sm font-medium">
                    <Checkbox checked={alerts[`canal_${canal}_activo`]} onCheckedChange={(v) => setAlerts({ ...alerts, [`canal_${canal}_activo`]: !!v })} data-testid={`config-canal-${canal}-activo`} />
                    {canal === "inapp" ? "Dentro de la app" : CANAL_LABELS[canal]}
                  </label>
                  {canal !== "inapp" && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox checked={alerts[`modo_simulacion_${canal}`]} onCheckedChange={(v) => setAlerts({ ...alerts, [`modo_simulacion_${canal}`]: !!v })} data-testid={`config-simulacion-${canal}`} />
                      Modo simulación (no envía de verdad, solo registra el texto exacto)
                    </label>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-primary" /> Credenciales Resend (email)</CardTitle></CardHeader>
            <CardContent className="grid max-w-lg gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">API Key {alerts.resend_api_key_configurado && <span className="text-green-700">(configurada, termina en •{alerts.resend_api_key_last4})</span>}</Label>
                <Input className="mt-1 font-mono" type="password" placeholder="re_..." value={secrets.resend_api_key} onChange={(e) => setSecrets({ ...secrets, resend_api_key: e.target.value })} data-testid="config-resend-api-key" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Email remitente</Label>
                <Input className="mt-1" value={alerts.resend_from_email || ""} onChange={(e) => setAlerts({ ...alerts, resend_from_email: e.target.value })} data-testid="config-resend-from-email" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Nombre remitente</Label>
                <Input className="mt-1" value={alerts.resend_from_name || ""} onChange={(e) => setAlerts({ ...alerts, resend_from_name: e.target.value })} data-testid="config-resend-from-name" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-primary" /> Credenciales Twilio (SMS / WhatsApp)</CardTitle></CardHeader>
            <CardContent className="grid max-w-lg gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Account SID {alerts.twilio_configurado && <span className="text-green-700">(configurado, termina en •{alerts.twilio_account_sid_last4})</span>}</Label>
                <Input className="mt-1 font-mono" type="password" placeholder="AC..." value={secrets.twilio_account_sid} onChange={(e) => setSecrets({ ...secrets, twilio_account_sid: e.target.value })} data-testid="config-twilio-sid" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Auth Token {alerts.twilio_configurado && <span className="text-green-700">(termina en •{alerts.twilio_auth_token_last4})</span>}</Label>
                <Input className="mt-1 font-mono" type="password" value={secrets.twilio_auth_token} onChange={(e) => setSecrets({ ...secrets, twilio_auth_token: e.target.value })} data-testid="config-twilio-token" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Número de origen SMS (E.164)</Label>
                <Input className="mt-1 font-mono" placeholder="+1..." value={alerts.twilio_sms_from || ""} onChange={(e) => setAlerts({ ...alerts, twilio_sms_from: e.target.value })} data-testid="config-twilio-sms-from" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Número de origen WhatsApp (E.164)</Label>
                <Input className="mt-1 font-mono" placeholder="+1..." value={alerts.twilio_whatsapp_from || ""} onChange={(e) => setAlerts({ ...alerts, twilio_whatsapp_from: e.target.value })} data-testid="config-twilio-whatsapp-from" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading">Plantillas de WhatsApp por tipo de alerta</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                WhatsApp exige plantillas pre-aprobadas para mensajes iniciados por el negocio. Indicá el Content SID (HX...) y las variables (en orden) para cada tipo de alerta.
              </p>
              {ALL_TIPOS_ALERTA.map((tipo) => (
                <div key={tipo} className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 sm:grid-cols-3">
                  <div className="text-sm font-medium">{ALERT_TYPE_LABELS[tipo]}</div>
                  <Input placeholder="Content SID (HX...)" className="font-mono text-xs" value={alerts.whatsapp_templates?.[tipo]?.content_sid || ""}
                    onChange={(e) => updateTemplate(tipo, "content_sid", e.target.value)} data-testid={`config-wa-template-sid-${tipo}`} />
                  <Input placeholder="variables separadas por coma (ej: numero_load,dias)" className="text-xs"
                    value={(alerts.whatsapp_templates?.[tipo]?.variables || []).join(",")}
                    onChange={(e) => updateTemplate(tipo, "variables", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                    data-testid={`config-wa-template-vars-${tipo}`} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading">Destinatarios</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(alerts.destinatarios || []).map((r, i) => (
                <div key={i} className="space-y-2 rounded-md border border-border p-3" data-testid={`config-recipient-${i}`}>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Input placeholder="Nombre" value={r.nombre} onChange={(e) => updateRecipient(i, "nombre", e.target.value)} data-testid={`config-recipient-${i}-nombre`} />
                    <Input placeholder="Email" value={r.email} onChange={(e) => updateRecipient(i, "email", e.target.value)} data-testid={`config-recipient-${i}-email`} />
                    <Input placeholder="Teléfono (+1...)" value={r.telefono} onChange={(e) => updateRecipient(i, "telefono", e.target.value)} data-testid={`config-recipient-${i}-telefono`} />
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="text-muted-foreground">Canales:</span>
                    {ALL_CANALES.map((c) => (
                      <label key={c} className="flex items-center gap-1">
                        <Checkbox checked={r.canales.includes(c)} onCheckedChange={() => toggleRecipientList(i, "canales", c)} data-testid={`config-recipient-${i}-canal-${c}`} /> {CANAL_LABELS[c]}
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="text-muted-foreground">Recibe:</span>
                    {ALL_TIPOS_ALERTA.map((t) => (
                      <label key={t} className="flex items-center gap-1">
                        <Checkbox checked={r.tipos_alerta.includes(t)} onCheckedChange={() => toggleRecipientList(i, "tipos_alerta", t)} data-testid={`config-recipient-${i}-tipo-${t}`} /> {ALERT_TYPE_LABELS[t]}
                      </label>
                    ))}
                  </div>
                  <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => removeRecipient(i)} data-testid={`config-recipient-${i}-remove`}>
                    <Trash2 className="h-3.5 w-3.5" /> Quitar destinatario
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addRecipient} className="gap-2" data-testid="config-add-recipient">
                <Plus className="h-3.5 w-3.5" /> Agregar destinatario
              </Button>
            </CardContent>
          </Card>

          <Button onClick={saveAlerts} data-testid="config-save-alertas">Guardar cambios</Button>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Send className="h-5 w-5 text-primary" /> Enviar alerta de prueba</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Canal</Label>
                <select className="mt-1 block rounded-md border border-input bg-background px-3 py-2 text-sm" value={testCanal} onChange={(e) => setTestCanal(e.target.value)} data-testid="config-test-canal">
                  {ALL_CANALES.map((c) => <option key={c} value={c}>{CANAL_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Destinatario (opcional — usa el primero configurado si se deja vacío)</Label>
                <Input className="mt-1 w-64" placeholder="email o teléfono" value={testDestinatario} onChange={(e) => setTestDestinatario(e.target.value)} data-testid="config-test-destinatario" />
              </div>
              <Button onClick={sendTestAlert} disabled={testing} className="gap-2" data-testid="config-send-test-alert">
                <Send className="h-4 w-4" /> {testing ? "Enviando..." : "Enviar alerta de prueba"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="integraciones" className="mt-5 space-y-5">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Plug className="h-5 w-5 text-primary" /> QuickBooks Online</CardTitle></CardHeader>
            <CardContent>
              <p className="mb-4 text-xs text-muted-foreground">
                Una conexión por compañía del grupo. Sin credenciales cargadas, la pantalla de <b>Coincidencias
                QuickBooks</b> (menú lateral) funciona con datos de ejemplo. Nunca se marca un cobro o pago
                automáticamente: siempre requiere confirmación humana en esa pantalla.
              </p>
              <div className="space-y-5">
                {qboConnections.map((conn) => (
                  <QuickBooksConnectionCard key={conn.company_id} conn={conn} onReload={loadQbo} />
                ))}
              </div>
            </CardContent>
          </Card>

          {companies.filter((c) => c.prefijo === "AL").map((c) => (
            <QuickBooksMapeosCard key={c.id} companyId={c.id} companyNombre={c.nombre} />
          ))}
        </TabsContent>

        <TabsContent value="permisos" className="mt-5 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading"><ShieldCheck className="h-5 w-5 text-primary" /> Matriz de permisos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex gap-2">
                {ROLES.map((r) => (
                  <Button key={r} size="sm" variant={selectedRole === r ? "default" : "outline"} onClick={() => setSelectedRole(r)} data-testid={`perm-role-${r}`}>
                    {ROLE_LABELS[r]}
                  </Button>
                ))}
              </div>
              {selectedRole === "dueno" && (
                <p className="mb-3 text-sm text-amber-700">El Dueño siempre tiene acceso total; los cambios acá no lo restringen.</p>
              )}
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2 text-left font-medium">Módulo</th>
                      {ACTIONS.map((a) => <th key={a} className="p-2 text-center font-medium capitalize">{a}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map((m) => (
                      <tr key={m} className="border-t border-border">
                        <td className="p-2 font-mono text-xs">{m}</td>
                        {ACTIONS.map((a) => (
                          <td key={a} className="p-2 text-center">
                            <Checkbox
                              checked={hasPerm(selectedRole, m, a)}
                              onCheckedChange={() => togglePerm(selectedRole, m, a)}
                              data-testid={`perm-${selectedRole}-${m}-${a}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <Button onClick={savePerms} data-testid="config-save-permisos">Guardar permisos</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
